import styled, { css, DefaultTheme } from 'styled-components';
import { ToastStatus } from 'src/context/notification/types';

const getColor = (theme: DefaultTheme, status: ToastStatus, border?: boolean) => {
  switch (status) {
    case 'error':
      return theme.co.error60;
    case 'warning':
      return theme.co.primary20;
    case 'success':
      return theme.co.success40;
    case 'info':
      return border ? theme.co.information80 : theme.co.information30;
  }
};

const getBackground = (theme: DefaultTheme, status: ToastStatus) => {
  switch (status) {
    case 'error':
      return theme.co.error95;
    case 'warning':
      return theme.co.primary98;
    case 'success':
      return theme.co.success99;
    case 'info':
      return theme.co.information98;
  }
};

export const Container = styled.div<{
  $status: ToastStatus;
  $border?: boolean;
  $pointedTip?: 'none' | 'left' | 'right';
}>`
  ${({ theme, $status, $border, $pointedTip }) => css`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: ${theme.sz.s4};
    color: ${getColor(theme, $status)};
    border-radius: ${theme.bo.mediumSmall};
    border: ${$border
      ? `${theme.bo.thinWidth} solid ${getColor(theme, $status, $border)}`
      : 'none'};
    position: relative;
    background: ${getBackground(theme, $status)};

    ${$border &&
    css`
      filter: drop-shadow(0px -1px 4px rgba(202, 202, 202, 0.1))
        drop-shadow(0px 0px 6px rgba(202, 202, 202, 0.2))
        drop-shadow(0px 6px 12px rgba(0, 0, 0, 0.1));
    `}

    ${$pointedTip !== 'none' &&
    css`
      &::before {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 10px 10px 10px;
        top: -10px;
        border-color: transparent transparent ${getColor(theme, $status, $border)} transparent;
        ${$pointedTip === 'left'
          ? css`
              left: ${theme.sz.s5};
            `
          : css`
              right: ${theme.sz.s5};
            `}
      }

      &::after {
        content: '';
        position: absolute;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 9px 9px 9px;
        top: -9px;
        border-color: transparent transparent ${getBackground(theme, $status)} transparent;
        ${$pointedTip === 'left'
          ? css`
              left: calc(${theme.sz.s5} + 1px);
            `
          : css`
              right: calc(${theme.sz.s5} + 1px);
            `}
      }
    `}
  `}
`;

export const TextContainer = styled.div(
  ({ theme }) => css`
    flex: 1;
    margin-left: ${theme.sz.s3};

    a {
      display: flex;
      padding: ${theme.sz.s4} ${theme.sz.s4} ${theme.sz.s4} 0;
      ${theme.typography.paragraph.base};
      color: ${theme.co.secondary30};
    }
  `,
);

export const Text = styled.p`
  ${({ theme }) => css`
    margin: 0;
    padding: ${theme.sz.s4} ${theme.sz.s4} ${theme.sz.s4} 0;
    color: ${theme.co.secondary30};
    overflow-wrap: anywhere;
    white-space: pre-line;

    @media (max-width: ${theme.sz.m}) {
      padding: ${theme.sz.s2} ${theme.sz.s2} ${theme.sz.s2} 0;
    }
  `}
`;

export const Footer = styled.div<{ $shiftRight?: boolean }>(
  ({ theme, $shiftRight }) => css`
    text-align: right;
    padding-bottom: ${theme.sz.s4};
    /* Negative margin so text goes below the X button */
    margin-right: ${$shiftRight ? `-${theme.sz.s4}` : theme.sz.s4};
  `,
);

export const Link = styled.a(
  ({ theme }) => css`
    ${theme.typography.button.l}
    text-decoration: none;
    color: inherit;
  `,
);

export const IconWrapper = styled.div`
  ${({ theme }) => css`
    float: left;
    margin: ${theme.sz.s2} 0 0 ${theme.sz.s4};
    font-size: ${theme.sz.s6};
  `}
`;

export const CloseIconWrapper = styled.button`
  ${({ theme }) => css`
    cursor: pointer;
    margin: 14px ${theme.sz.s2} 0 0;
    color: ${theme.co.secondary30};
    background: none;
    border: none;
    font-size: ${theme.sz.s4};
  `}
`;

export const Backdrop = styled.div(
  ({ theme }) => css`
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: ${theme.zi.header};
  `,
);
